import {Component, inject, Input, OnDestroy} from '@angular/core';
import {catchError, combineLatest, EMPTY, map, Observable, of, Subject, switchMap, takeUntil, tap} from "rxjs";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatButton} from "@angular/material/button";
import {MatSelectModule} from "@angular/material/select";
import {MatPaginatorModule, PageEvent} from "@angular/material/paginator";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {IDropdownOption} from "../../../interfaces/IDropDownOptions";
import {charityOption} from "../../../interfaces/charity/ICharityNameForGame";
import {ICharityAdjustment} from "../../../interfaces/charity/ICharityAdjustment";
import {ICharityBalance} from "../../../interfaces/charity/ICharityBalance";
import {CharityPayoutService} from "../../../services/charity-payout.service";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {SnackbarService} from "../../../services/snackbar.service";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {PipesModule} from "../../../pipes/pipes.module";
import {LoadingCardContentComponent} from "../../../shared/loading-card-content/loading-card-content.component";
import {HasPermissionDirective} from "../../../directives/permissions/has-permission";
import {PermissionTypes} from "../../../enum/PermissionTypes";
import {
  TableExportButtonsComponent
} from "../../../components/table-export-buttons/table-export-buttons.component";
import {ITableExportColumn, ITableExportRequest, TableExportService} from "../../../services/table-export.service";
import {FormatCurrencyPipe} from "../../../pipes/format-currency.pipe";

/**
 * Adjustments and chargebacks for one charity (Phase 3 of retiring the Google-Sheets payout ledgers).
 *
 * A chargeback is booked here rather than by voiding the payout it relates to: the payout stays a truthful
 * record of money that moved, and the loss comes off what the charity is owed next. The form therefore talks
 * about "reduce" and "increase" rather than signed numbers — an admin should not have to remember that a
 * chargeback is typed as a negative.
 */
@Component({
  selector: 'app-charity-adjustments',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatButton,
    MatSelectModule,
    PipesModule,
    LoadingCardContentComponent,
    HasPermissionDirective,
    TableExportButtonsComponent,
    MatPaginatorModule
  ],
  templateUrl: './charity-adjustments.component.html',
  styleUrls: ['./charity-adjustments.component.scss']
})
export class CharityAdjustmentsComponent implements OnDestroy {

  /** Not a charity id, and cannot collide with one: every real option is a GUID. */
  public static readonly ALL_CHARITIES_OPTION_ID = 'all-charities';

  private charityPayoutService: CharityPayoutService = inject(CharityPayoutService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private tableExportService: TableExportService = inject(TableExportService);
  private currencyPipe: FormatCurrencyPipe = inject(FormatCurrencyPipe);
  private destroy$ = new Subject<void>();

  /** Every refresh is queued here so switchMap can cancel the previous filter's read. See refresh(). */
  private refresh$ = new Subject<void>();

  constructor() {
    this.refresh$
      .pipe(
        tap(() => this.uiState = UIStateEnum.ShowLoading),
        switchMap(() => this.readForCurrentFilter()),
        takeUntil(this.destroy$)
      )
      .subscribe((resultP) => {
        this.adjustments = resultP.adjustments;
        this.balance = resultP.balance;
        // Back to the first page whenever the list changes underneath: page 4 of a one-charity list is a
        // blank table that looks like missing data.
        this.pageIndex = 0;
        this.uiState = UIStateEnum.ShowData;
      });
  }

  @Input() set gameData(gameP: IGameQueryResult) {
    this.activeGame = gameP;
    this.charityOptions = [];
    this.selectedCharityId = null;
    this.adjustments = [];
    this.balance = null;
    this.fetchCharitiesForGame();
    this.refresh();
  }

  public activeGame: IGameQueryResult | null = null;
  public charityOptions: IDropdownOption[] = [];
  public selectedCharityId: string | null = null;
  public adjustments: ICharityAdjustment[] = [];
  /**
   * The game-wide list spans every charity, so it is paged. The export deliberately covers the whole filtered
   * list rather than the page on screen — a page of a ledger is not a ledger.
   */
  public pageIndex = 0;
  public pageSize = 50;
  public balance: ICharityBalance | null = null;
  public isSubmitting = false;
  public uiState = UIStateEnum.ShowData;
  protected readonly UIStateEnum = UIStateEnum;
  protected readonly permissionTypes = PermissionTypes;

  public adjustmentForm = new FormGroup({
    // Direction is explicit rather than expecting a signed amount: "reduce" is the common case (a
    // chargeback) and typing a minus sign is the kind of slip that moves money the wrong way.
    direction: new FormControl<'reduce' | 'increase'>('reduce', {nonNullable: true}),
    amount: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    reason: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(500)]),
    effectiveDate: new FormControl<string | null>(CharityAdjustmentsComponent.todayIso())
  });

  /**
   * Today as yyyy-MM-dd in the admin's local calendar. Not toISOString(): that converts to UTC, so an admin
   * west of UTC entering an adjustment in the evening would pre-fill tomorrow — which the server rejects as
   * a future date.
   */
  private static todayIso(): string {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');

    return `${now.getFullYear()}-${month}-${day}`;
  }

  /** The rows on the current page. */
  get pagedAdjustments(): ICharityAdjustment[] {
    const start = this.pageIndex * this.pageSize;

    return this.adjustments.slice(start, start + this.pageSize);
  }

  onPageChange(eventP: PageEvent): void {
    this.pageIndex = eventP.pageIndex;
    this.pageSize = eventP.pageSize;
  }

  /** What the select shows while no single charity is chosen. */
  get allCharitiesOptionId(): string {
    return CharityAdjustmentsComponent.ALL_CHARITIES_OPTION_ID;
  }

  /** Maximum date the picker offers, so a future date is refused before the request is made. */
  get today(): string {
    return CharityAdjustmentsComponent.todayIso();
  }

  get canSubmit(): boolean {
    return !!this.selectedCharityId && this.adjustmentForm.valid && !this.isSubmitting;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Null is the "All charities" entry: the whole game's ledger, which is what the tab now opens on. */
  onCharitySelected(charityIdP: string | null): void {
    this.selectedCharityId = charityIdP === CharityAdjustmentsComponent.ALL_CHARITIES_OPTION_ID ? null : charityIdP;

    if (!this.selectedCharityId) {
      // Cleared now rather than when the read resolves, so the previous charity's position does not sit on screen under
      // a heading that no longer names it. switchMap is what stops the outgoing read putting it back.
      this.balance = null;
    }

    this.refresh();
  }

  /**
   * The adjustments on show: every one in the game, or one charity's, depending on the filter. Before #1411 an
   * unfiltered view did not exist — the tab was an empty dropdown until a charity was chosen, so "what
   * chargebacks came in this week" could not be asked at all.
   *
   * Requests go through the subject rather than being fired here, so switchMap cancels the read the previous
   * filter started. Two reads with only takeUntil(destroy$) between them meant switching All → charity A →
   * charity B left whichever response landed last on screen, which needn't be B's. Now that "all ↔ one" is a
   * routine action rather than once per visit, the window is wide enough to hit.
   */
  refresh(): void {
    if (!this.activeGame) return;

    this.refresh$.next();
  }

  /**
   * The adjustments and, for a single charity, its balance — read as one unit so a late balance cannot arrive
   * beside another charity's adjustments, which would state one charity's position over another's ledger.
   */
  private readForCurrentFilter(): Observable<{ adjustments: ICharityAdjustment[], balance: ICharityBalance | null }> {
    // Captured before the request rather than read in the callback: by the time it resolves the filter may
    // have moved on, and this read belongs to the filter that started it.
    const game_id = this.activeGame!.Id;
    const charity_id = this.selectedCharityId;

    const adjustments$ = charity_id
      ? this.charityPayoutService.fetchAdjustmentsByCharityId(charity_id, game_id)
      : this.charityPayoutService.fetchAdjustmentsByGameId(game_id);

    // No single charity, no single balance: the strip states one charity's position, and summing it across the
    // game would be a different figure wearing the same label.
    const balance$ = charity_id
      ? this.charityPayoutService.fetchBalanceByCharityId(charity_id, game_id)
      : of(null);

    return combineLatest([adjustments$, balance$]).pipe(
      map(([adjustmentsP, balanceP]) => ({adjustments: adjustmentsP ?? [], balance: balanceP})),
      catchError((err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);

        // Swallowed rather than rethrown: an error on one read must not tear down the pipeline every later
        // filter change depends on.
        return EMPTY;
      })
    );
  }

  onSubmit(): void {
    if (!this.canSubmit || !this.activeGame || !this.selectedCharityId) return;

    this.isSubmitting = true;
    const form_value = this.adjustmentForm.getRawValue();
    // The form collects dollars and a direction; the API takes signed cents.
    const magnitude = Math.round((form_value.amount ?? 0) * 100);

    this.charityPayoutService.createCharityAdjustment({
      GameId: this.activeGame.Id,
      CharityId: this.selectedCharityId,
      Amount: form_value.direction === 'reduce' ? -magnitude : magnitude,
      Reason: form_value.reason?.trim() ?? '',
      EffectiveDate: form_value.effectiveDate || null,
      PlayerPaymentId: null
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackbarService.openSuccessfulSnackBar('Adjustment recorded successfully!');
        this.isSubmitting = false;
        this.resetForm();
        this.refresh();
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
        this.isSubmitting = false;
      }
    });
  }

  onVoidClick(adjustmentIdP: string): void {
    if (!this.activeGame) return;

    this.charityPayoutService.voidCharityAdjustment(this.activeGame.Id, adjustmentIdP)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snackbarService.openSuccessfulSnackBar('Adjustment voided successfully!');
          this.refresh();
        },
        error: (err) => this.errorHandlingService.displayPageLevelErrorMessage(err)
      });
  }

  /**
   * The list as it stands, in the order shown. The signed amount is exported as it reads on screen — a
   * chargeback exported as a positive number would invert its meaning in the one artefact that leaves the app.
   */
  private buildExportRequest(): ITableExportRequest<ICharityAdjustment> {
    const selected_charity_name = this.charityOptions.find(optionP => optionP.id === this.selectedCharityId)?.name;

    const charity_column: ITableExportColumn<ICharityAdjustment>[] = this.selectedCharityId
      ? []
      : [{header: 'Charity', value: adjustmentP => adjustmentP.CharityName ?? '—'}];

    const summary = this.balance
      ? [
          {label: 'Earned', value: this.currencyPipe.transform(this.balance.TotalContributions)},
          {label: 'Paid out', value: this.currencyPipe.transform(this.balance.TotalPayouts)},
          {label: 'Adjustments', value: this.currencyPipe.transform(this.balance.TotalAdjustments)},
          {label: 'Owed', value: this.currencyPipe.transform(this.balance.AmountOwed)}
        ]
      : [{label: 'Entries', value: `${this.adjustments.length}`}];

    return {
      title: 'Adjustments and chargebacks',
      subtitle: [this.activeGame?.Name, selected_charity_name || 'All charities'].filter(Boolean).join(' — '),
      summary,
      rows: this.adjustments,
      columns: [
        ...charity_column,
        {
          header: 'Effective date',
          value: adjustmentP => this.formatDate(adjustmentP.EffectiveDate),
          csvValue: adjustmentP => this.tableExportService.csvDate(adjustmentP.EffectiveDate)
        },
        {
          header: 'Amount',
          value: adjustmentP => this.currencyPipe.transform(adjustmentP.Amount),
          // Signed, so a chargeback stays a negative number a spreadsheet can total against the payouts.
          csvValue: adjustmentP => this.tableExportService.csvMoney(adjustmentP.Amount),
          numeric: true
        },
        {header: 'Reason', value: adjustmentP => adjustmentP.Reason},
        {header: 'Status', value: adjustmentP => adjustmentP.Voided ? 'Voided' : 'Applied'}
      ],
      fileName: this.tableExportService.fileNameFor([
        this.activeGame?.ShortName || this.activeGame?.Name,
        selected_charity_name || 'all-charities',
        'adjustments'
      ])
    };
  }

  onExportCsv(): void {
    this.tableExportService.exportCsv(this.buildExportRequest());
  }

  onExportPdf(): void {
    this.tableExportService.exportPdf(this.buildExportRequest());
  }

  /**
   * Formats a calendar date without going through a timezone conversion. effective_date is a date, not an
   * instant: the API serializes it as UTC midnight, so `new Date(v).toLocaleDateString()` renders the
   * previous day for any admin west of UTC and misdates the adjustment across period cutoffs.
   */
  formatDate(valueP: string): string {
    if (!valueP) return '—';

    const [date_part] = valueP.split('T');
    const [year, month, day] = date_part.split('-');

    return `${month}/${day}/${year}`;
  }

  private resetForm(): void {
    this.adjustmentForm.reset({
      direction: 'reduce',
      amount: null,
      reason: null,
      effectiveDate: CharityAdjustmentsComponent.todayIso()
    });
  }

  private fetchCharitiesForGame(): void {
    if (!this.activeGame) return;

    // Names only: the per-charity contribution and QR-code totals the charities-by-category read computes are not on
    // this screen, and paying for them is what made the picker slow to fill (#1505).
    this.charityPayoutService.fetchCharityNamesByGameIdForAdjustments(this.activeGame.Id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (charitiesP) => {
          // "All charities" first and selected by default, so the whole game's ledger is what you land on and
          // narrowing is a deliberate act rather than the price of admission.
          this.charityOptions = [
            {id: CharityAdjustmentsComponent.ALL_CHARITIES_OPTION_ID, name: 'All charities'},
            ...charitiesP.map(charityP => charityOption(charityP))
          ];
        },
        error: (err) => this.errorHandlingService.displayPageLevelErrorMessage(err)
      });
  }
}
